/* ═══════════════════════════════════════════════════════════════════════════
   modStrings.js — the moderation-outcome copy, in the reader's language.

   🟥 THE SERVER STORES A DECISION, NOT A SENTENCE. moderationNotify.js writes an
   `outcome` ('removed' | 'kept') and a frozen preview of the post, and nothing
   else — no prose. Every client renders the words itself. This is the web's half
   of that contract, word for word with allo-app/locales/*.json.

   🟥 THE REST OF THIS WEB APP IS ENGLISH-ONLY. There is no i18n framework here
   and this file does not pretend to be one — it localises these eleven strings
   and nothing more, off the BROWSER's language, because a person who reports a
   post from a phone in Arabic and opens the outcome on the web should not be
   handed English. When the web does get a real language picker, this table is
   the seed and `pickLang()` is the one place to re-point.

   🟥 A MISSING KEY MUST NOT RENDER ITS OWN NAME. That has shipped in this
   codebase before (guest_joined_body). mt() falls back to English and, failing
   that, returns '' — an empty line is a bug you notice; `report_update_body` in
   somebody's notification tray is a bug you ship.
   ═══════════════════════════════════════════════════════════════════════════ */

const T = {
  "vibe_notif_report_update": {
    "en": "We have an update on your report",
    "es": "Tenemos novedades sobre tu reporte",
    "fr": "Nous avons du nouveau sur votre signalement",
    "de": "Wir haben ein Update zu deiner Meldung",
    "zh": "我们对您的举报有了处理结果",
    "ja": "ご報告について更新があります",
    "ru": "У нас есть обновление по вашей жалобе",
    "ar": "لدينا تحديث بشأن بلاغك",
    "pt": "Temos uma atualização sobre sua denúncia",
    "hi": "आपकी रिपोर्ट पर हमारे पास एक अपडेट है"
  },
  "vibe_notif_post_action": {
    "en": "An action was taken on your post",
    "es": "Se tomó una acción sobre tu publicación",
    "fr": "Une mesure a été prise concernant votre publication",
    "de": "Zu deinem Beitrag wurde eine Maßnahme ergriffen",
    "zh": "我们对您的帖子采取了处理措施",
    "ja": "あなたの投稿に対して対応が行われました",
    "ru": "По вашей публикации принято решение",
    "ar": "تم اتخاذ إجراء بشأن منشورك",
    "pt": "Uma ação foi tomada sobre sua publicação",
    "hi": "आपकी पोस्ट पर कार्रवाई की गई है"
  },
  "vibe_report_outcome_title": {
    "en": "Update on your report",
    "es": "Novedades sobre tu reporte",
    "fr": "Mise à jour de votre signalement",
    "de": "Update zu deiner Meldung",
    "zh": "您的举报有更新",
    "ja": "報告に関する更新",
    "ru": "Обновление по вашей жалобе",
    "ar": "تحديث بشأن بلاغك",
    "pt": "Atualização sobre sua denúncia",
    "hi": "आपकी रिपोर्ट पर अपडेट"
  },
  "vibe_post_action_title": {
    "en": "Action on your post",
    "es": "Acción sobre tu publicación",
    "fr": "Action sur votre publication",
    "de": "Maßnahme zu deinem Beitrag",
    "zh": "对您帖子的处理",
    "ja": "投稿への対応",
    "ru": "Действие с вашей публикацией",
    "ar": "إجراء بشأن منشورك",
    "pt": "Ação sobre sua publicação",
    "hi": "आपकी पोस्ट पर कार्रवाई"
  },
  "vibe_report_outcome_removed": {
    "en": "After reviewing your report, we decided to remove this post. Thank you for helping keep Allo safe for everyone.",
    "es": "Tras revisar tu reporte, decidimos eliminar esta publicación. Gracias por ayudar a mantener Allo seguro para todos.",
    "fr": "Après examen de votre signalement, nous avons décidé de supprimer cette publication. Merci de contribuer à la sécurité d’Allo pour tous.",
    "de": "Nach Prüfung deiner Meldung haben wir diesen Beitrag entfernt. Danke, dass du Allo für alle sicher hältst.",
    "zh": "在审核您的举报后，我们决定删除这条帖子。感谢您帮助维护 Allo 的安全环境。",
    "ja": "ご報告を確認し、この投稿を削除しました。Allo を安全な場所に保つためのご協力ありがとうございます。",
    "ru": "Рассмотрев вашу жалобу, мы удалили эту публикацию. Спасибо, что помогаете сохранять Allo безопасным для всех.",
    "ar": "بعد مراجعة بلاغك، قررنا حذف هذا المنشور. شكرًا لمساعدتك في الحفاظ على أمان Allo للجميع.",
    "pt": "Após analisar sua denúncia, decidimos remover esta publicação. Obrigado por ajudar a manter o Allo seguro para todos.",
    "hi": "आपकी रिपोर्ट की समीक्षा करने के बाद, हमने यह पोस्ट हटाने का निर्णय लिया। Allo को सभी के लिए सुरक्षित रखने में मदद करने के लिए धन्यवाद।"
  },
  "vibe_report_outcome_kept": {
    "en": "After carefully reviewing your report, we decided to keep this post, as it does not violate the Allo community guidelines. Thank you for helping keep our community safe.",
    "es": "Tras revisar cuidadosamente tu reporte, decidimos mantener esta publicación, ya que no infringe las normas de la comunidad de Allo. Gracias por ayudar a mantener segura nuestra comunidad.",
    "fr": "Après un examen attentif de votre signalement, nous avons décidé de conserver cette publication, car elle n’enfreint pas les règles de la communauté Allo. Merci de contribuer à la sécurité de notre communauté.",
    "de": "Nach sorgfältiger Prüfung deiner Meldung behalten wir diesen Beitrag, da er nicht gegen die Allo-Community-Richtlinien verstößt. Danke, dass du unsere Community sicher hältst.",
    "zh": "在仔细审核您的举报后，我们决定保留这条帖子，因为它并未违反 Allo 社区准则。感谢您帮助维护社区安全。",
    "ja": "ご報告を慎重に確認した結果、この投稿は Allo のコミュニティガイドラインに違反していないため、そのままとしました。コミュニティの安全を守るためのご協力ありがとうございます。",
    "ru": "Внимательно рассмотрев вашу жалобу, мы решили оставить эту публикацию, так как она не нарушает правила сообщества Allo. Спасибо, что помогаете сохранять наше сообщество безопасным.",
    "ar": "بعد مراجعة بلاغك بعناية، قررنا الإبقاء على هذا المنشور لأنه لا ينتهك إرشادات مجتمع Allo. شكرًا لمساعدتك في الحفاظ على أمان مجتمعنا.",
    "pt": "Após analisar cuidadosamente sua denúncia, decidimos manter esta publicação, pois ela não viola as diretrizes da comunidade Allo. Obrigado por ajudar a manter nossa comunidade segura.",
    "hi": "आपकी रिपोर्ट की सावधानीपूर्वक समीक्षा करने के बाद, हमने यह पोस्ट रखने का निर्णय लिया, क्योंकि यह Allo सामुदायिक दिशानिर्देशों का उल्लंघन नहीं करती। हमारे समुदाय को सुरक्षित रखने में मदद करने के लिए धन्यवाद।"
  },
  "vibe_post_action_removed": {
    "en": "We received reports about your post. After reviewing it, we decided that it violates our guidelines and community rules, and it has been removed.",
    "es": "Recibimos reportes sobre tu publicación. Tras revisarla, determinamos que infringe nuestras normas y reglas de la comunidad, y ha sido eliminada.",
    "fr": "Nous avons reçu des signalements concernant votre publication. Après examen, nous avons déterminé qu’elle enfreint nos règles et celles de la communauté, et elle a été supprimée.",
    "de": "Wir haben Meldungen zu deinem Beitrag erhalten. Nach Prüfung haben wir festgestellt, dass er gegen unsere Richtlinien und Community-Regeln verstößt, und ihn entfernt.",
    "zh": "我们收到了关于您帖子的举报。经审核，我们认定它违反了我们的准则和社区规则，该帖子已被删除。",
    "ja": "あなたの投稿について報告を受け取りました。確認の結果、ガイドラインおよびコミュニティルールに違反していると判断し、削除しました。",
    "ru": "Мы получили жалобы на вашу публикацию. Рассмотрев её, мы установили, что она нарушает наши правила и правила сообщества, и удалили её.",
    "ar": "تلقينا بلاغات بشأن منشورك. وبعد مراجعته، قررنا أنه ينتهك إرشاداتنا وقواعد المجتمع، وتمت إزالته.",
    "pt": "Recebemos denúncias sobre sua publicação. Após analisá-la, determinamos que ela viola nossas diretrizes e regras da comunidade, e ela foi removida.",
    "hi": "हमें आपकी पोस्ट के बारे में रिपोर्टें मिलीं। समीक्षा के बाद, हमने पाया कि यह हमारे दिशानिर्देशों और सामुदायिक नियमों का उल्लंघन करती है, और इसे हटा दिया गया है।"
  },
  "vibe_report_view_post": {
    "en": "View post",
    "es": "Ver publicación",
    "fr": "Voir la publication",
    "de": "Beitrag ansehen",
    "zh": "查看帖子",
    "ja": "投稿を見る",
    "ru": "Посмотреть публикацию",
    "ar": "عرض المنشور",
    "pt": "Ver publicação",
    "hi": "पोस्ट देखें"
  },
  "vibe_ui_allo_team": {
    "en": "Allo Team",
    "es": "Equipo Allo",
    "fr": "Équipe Allo",
    "de": "Allo-Team",
    "zh": "Allo 团队",
    "ja": "Allo チーム",
    "ru": "Команда Allo",
    "ar": "فريق Allo",
    "pt": "Equipe Allo",
    "hi": "Allo टीम"
  },
  "vibe_ui_post_removed": {
    "en": "Post Removed",
    "es": "Publicación eliminada",
    "fr": "Post supprimé",
    "de": "Beitrag entfernt",
    "zh": "帖子已删除",
    "ja": "投稿が削除されました",
    "ru": "Пост удален",
    "ar": "تمت إزالة المنشور",
    "pt": "Publicação removida",
    "hi": "पोस्ट हटा दी गई"
  },
  "vibe_ui_i_understand": {
    "en": "I Understand",
    "es": "Entiendo",
    "fr": "Je comprends",
    "de": "Ich verstehe",
    "zh": "我明白了",
    "ja": "了解しました",
    "ru": "Я понимаю",
    "ar": "أفهم ذلك",
    "pt": "Eu entendo",
    "hi": "मैं समझता हूँ"
  },
  "vibe_notif_comment_action": {
    "en": "Your comment was removed",
    "es": "Tu comentario fue eliminado",
    "fr": "Votre commentaire a été supprimé",
    "de": "Dein Kommentar wurde entfernt",
    "zh": "您的评论已被删除",
    "ja": "あなたのコメントは削除されました",
    "ru": "Ваш комментарий удалён",
    "ar": "تم حذف تعليقك",
    "pt": "Seu comentário foi removido",
    "hi": "आपकी टिप्पणी हटा दी गई"
  },
  "vibe_comment_action_title": {
    "en": "Your comment was removed",
    "es": "Tu comentario fue eliminado",
    "fr": "Votre commentaire a été supprimé",
    "de": "Dein Kommentar wurde entfernt",
    "zh": "您的评论已被删除",
    "ja": "あなたのコメントは削除されました",
    "ru": "Ваш комментарий удалён",
    "ar": "تم حذف تعليقك",
    "pt": "Seu comentário foi removido",
    "hi": "आपकी टिप्पणी हटा दी गई"
  },
  "vibe_comment_action_removed": {
    "en": "We reviewed your comment and removed it, because it goes against the Allo community guidelines.",
    "es": "Revisamos tu comentario y lo eliminamos porque incumple las normas de la comunidad de Allo.",
    "fr": "Nous avons examiné votre commentaire et l’avons supprimé, car il enfreint les règles de la communauté Allo.",
    "de": "Wir haben deinen Kommentar geprüft und entfernt, da er gegen die Community-Richtlinien von Allo verstößt.",
    "zh": "我们审核了您的评论并将其删除，因为它违反了 Allo 社区准则。",
    "ja": "あなたのコメントを確認し、Allo のコミュニティガイドラインに違反しているため削除しました。",
    "ru": "Мы рассмотрели ваш комментарий и удалили его, так как он нарушает правила сообщества Allo.",
    "ar": "راجعنا تعليقك وقمنا بحذفه لأنه يخالف إرشادات مجتمع Allo.",
    "pt": "Analisamos seu comentário e o removemos porque ele viola as diretrizes da comunidade Allo.",
    "hi": "हमने आपकी टिप्पणी की समीक्षा की और उसे हटा दिया, क्योंकि वह Allo सामुदायिक दिशानिर्देशों का उल्लंघन करती है।"
  },
  "vibe_post_action_removed_noreports": {
    "en": "We reviewed your post and removed it, because it goes against the Allo community guidelines.",
    "es": "Revisamos tu publicación y la eliminamos porque incumple las normas de la comunidad de Allo.",
    "fr": "Nous avons examiné votre publication et l’avons supprimée, car elle enfreint les règles de la communauté Allo.",
    "de": "Wir haben deinen Beitrag geprüft und entfernt, da er gegen die Community-Richtlinien von Allo verstößt.",
    "zh": "我们审核了您的帖子并将其删除，因为它违反了 Allo 社区准则。",
    "ja": "あなたの投稿を確認し、Allo のコミュニティガイドラインに違反しているため削除しました。",
    "ru": "Мы рассмотрели вашу публикацию и удалили её, так как она нарушает правила сообщества Allo.",
    "ar": "راجعنا منشورك وقمنا بحذفه لأنه يخالف إرشادات مجتمع Allo.",
    "pt": "Analisamos sua publicação e a removemos porque ela viola as diretrizes da comunidade Allo.",
    "hi": "हमने आपकी पोस्ट की समीक्षा की और उसे हटा दिया, क्योंकि वह Allo सामुदायिक दिशानिर्देशों का उल्लंघन करती है।"
  },
  "vibe_mod_reason_label": {
    "en": "Reason: {{reason}}",
    "es": "Motivo: {{reason}}",
    "fr": "Motif : {{reason}}",
    "de": "Grund: {{reason}}",
    "zh": "原因：{{reason}}",
    "ja": "理由: {{reason}}",
    "ru": "Причина: {{reason}}",
    "ar": "السبب: {{reason}}",
    "pt": "Motivo: {{reason}}",
    "hi": "कारण: {{reason}}"
  },
  "vibe_report_other": {
    "en": "Breaking the community guidelines",
    "es": "Incumplir las normas de la comunidad",
    "fr": "Non-respect des règles de la communauté",
    "de": "Verstoß gegen die Community-Richtlinien",
    "zh": "违反社区准则",
    "ja": "コミュニティガイドライン違反",
    "ru": "Нарушение правил сообщества",
    "ar": "مخالفة إرشادات المجتمع",
    "pt": "Violação das diretrizes da comunidade",
    "hi": "सामुदायिक दिशानिर्देशों का उल्लंघन"
  },
  "vibe_mod_your_comment": {
    "en": "Your comment",
    "es": "Tu comentario",
    "fr": "Votre commentaire",
    "de": "Dein Kommentar",
    "zh": "您的评论",
    "ja": "あなたのコメント",
    "ru": "Ваш комментарий",
    "ar": "تعليقك",
    "pt": "Seu comentário",
    "hi": "आपकी टिप्पणी"
  },
  "vibe_report_nudes": {
    "en": "Nudes or Sexual Content",
    "es": "Desnudos o contenido sexual",
    "fr": "Nudité ou contenu sexuel",
    "de": "Nacktheit oder sexuelle Inhalte",
    "zh": "裸露或性内容",
    "ja": "ヌードまたは性的内容",
    "ru": "Нагота или сексуальный контент",
    "ar": "عري أو محتوى جنسي",
    "pt": "Nudes ou conteúdo sexual",
    "hi": "नग्नता या यौन सामग्री"
  },
  "vibe_report_violence": {
    "en": "Violence or Gore",
    "es": "Violencia o contenido sangriento",
    "fr": "Violence ou gore",
    "de": "Gewalt oder Gore",
    "zh": "暴力或血腥",
    "ja": "暴力またはゴア表現",
    "ru": "Насилие или кровь",
    "ar": "عنف أو مشاهد دموية",
    "pt": "Violência ou sangue",
    "hi": "हिंसा या डरावनी सामग्री"
  },
  "vibe_report_hatred": {
    "en": "Hatred or Bullying",
    "es": "Odio o acoso",
    "fr": "Haine ou harcèlement",
    "de": "Hass oder Mobbing",
    "zh": "仇恨或欺凌",
    "ja": "憎悪または嫌がらせ",
    "ru": "Ненависть или издевательства",
    "ar": "كراهية أو تنمر",
    "pt": "Ódio ou bullying",
    "hi": "घृणा या बदमाशी"
  },
  "vibe_report_spam": {
    "en": "Spam or Scam",
    "es": "Spam o estafa",
    "fr": "Spam ou escroquerie",
    "de": "Spam oder Betrug",
    "zh": "垃圾信息或欺诈",
    "ja": "スパムまたは詐欺",
    "ru": "Спам или мошенничество",
    "ar": "محتوى غير مرغوب فيه أو احتيال",
    "pt": "Spam ou golpe",
    "hi": "स्पैम या धोखाधड़ी"
  },
  "rights_muted_title": {
    "en": "Sound removed from your video",
    "es": "Se quitó el sonido de tu video",
    "fr": "Son retiré de votre vidéo",
    "de": "Ton aus deinem Video entfernt",
    "zh": "您的视频已被移除声音",
    "ja": "あなたの動画から音声が削除されました",
    "ru": "Из вашего видео удалён звук",
    "ar": "تمت إزالة الصوت من فيديوك",
    "pt": "O som foi removido do seu vídeo",
    "hi": "आपके वीडियो से आवाज़ हटा दी गई"
  },
  "rights_muted_push": {
    "en": "We found copyrighted music in it, so it now plays without sound. Tap to see why and what you can do.",
    "es": "Encontramos música con derechos de autor, así que ahora se reproduce sin sonido. Toca para ver por qué y qué puedes hacer.",
    "fr": "Nous y avons trouvé de la musique protégée par le droit d'auteur : elle est désormais lue sans son. Touchez pour savoir pourquoi et ce que vous pouvez faire.",
    "de": "Wir haben urheberrechtlich geschützte Musik darin gefunden, deshalb läuft es jetzt ohne Ton. Tippe, um zu sehen, warum und was du tun kannst.",
    "zh": "我们在其中发现了受版权保护的音乐，因此它现在以静音播放。点按查看原因和您可以采取的措施。",
    "ja": "著作権で保護された音楽が含まれていたため、現在は無音で再生されます。タップして理由と対処方法を確認してください。",
    "ru": "Мы обнаружили в нём музыку, защищённую авторским правом, поэтому теперь оно воспроизводится без звука. Нажмите, чтобы узнать почему и что можно сделать.",
    "ar": "وجدنا فيه موسيقى محمية بحقوق النشر، لذا يُعرض الآن دون صوت. اضغط لمعرفة السبب وما يمكنك فعله.",
    "pt": "Encontramos música protegida por direitos autorais nele, então agora ele toca sem som. Toque para ver o motivo e o que você pode fazer.",
    "hi": "हमें इसमें कॉपीराइट वाला संगीत मिला, इसलिए अब यह बिना आवाज़ के चलता है। कारण और आप क्या कर सकते हैं, देखने के लिए टैप करें।"
  },
  "rights_notif_muted": {
    "en": "The sound was removed from one of your videos",
    "es": "Se quitó el sonido de uno de tus videos",
    "fr": "Le son a été retiré de l'une de vos vidéos",
    "de": "Der Ton wurde aus einem deiner Videos entfernt",
    "zh": "您的一个视频已被移除声音",
    "ja": "あなたの動画のひとつから音声が削除されました",
    "ru": "Из одного из ваших видео удалён звук",
    "ar": "تمت إزالة الصوت من أحد فيديوهاتك",
    "pt": "O som foi removido de um dos seus vídeos",
    "hi": "आपके एक वीडियो से आवाज़ हटा दी गई"
  },
  "rights_muted_why_title": {
    "en": "Why this happened",
    "es": "Por qué ocurrió",
    "fr": "Pourquoi cela est arrivé",
    "de": "Warum das passiert ist",
    "zh": "为什么会这样",
    "ja": "なぜこうなったのか",
    "ru": "Почему это произошло",
    "ar": "لماذا حدث هذا",
    "pt": "Por que isso aconteceu",
    "hi": "ऐसा क्यों हुआ"
  },
  "rights_muted_why": {
    "en": "A music-recognition check matched the audio in your video to {{track}}. Allo can't play music its owner hasn't licensed for use here, so the sound was removed.",
    "es": "Una comprobación de reconocimiento musical identificó el audio de tu video como {{track}}. Allo no puede reproducir música que su titular no haya licenciado para usarse aquí, así que se quitó el sonido.",
    "fr": "Une vérification de reconnaissance musicale a associé l'audio de votre vidéo à {{track}}. Allo ne peut pas diffuser une musique que son ayant droit n'a pas autorisée ici : le son a donc été retiré.",
    "de": "Eine Musikerkennung hat den Ton deines Videos {{track}} zugeordnet. Allo darf keine Musik abspielen, die der Rechteinhaber hier nicht lizenziert hat – deshalb wurde der Ton entfernt.",
    "zh": "音乐识别检查将您视频中的音频匹配为 {{track}}。Allo 无法播放版权所有者未授权在此使用的音乐，因此声音已被移除。",
    "ja": "音楽認識チェックにより、動画の音声が「{{track}}」と一致しました。Allo では権利者が許諾していない音楽を再生できないため、音声を削除しました。",
    "ru": "Проверка распознавания музыки сопоставила аудио вашего видео с {{track}}. Allo не может воспроизводить музыку, которую правообладатель не лицензировал для использования здесь, поэтому звук был удалён.",
    "ar": "طابق فحص التعرف على الموسيقى الصوت في فيديوك مع {{track}}. لا يمكن لـ Allo تشغيل موسيقى لم يرخّص مالكها استخدامها هنا، لذا تمت إزالة الصوت.",
    "pt": "Uma verificação de reconhecimento musical identificou o áudio do seu vídeo como {{track}}. O Allo não pode reproduzir música que o titular não licenciou para uso aqui, então o som foi removido.",
    "hi": "संगीत पहचान जाँच ने आपके वीडियो की आवाज़ को {{track}} से मिलाया। Allo ऐसा संगीत नहीं चला सकता जिसे उसके मालिक ने यहाँ इस्तेमाल के लिए लाइसेंस न दिया हो, इसलिए आवाज़ हटा दी गई।"
  },
  "rights_muted_why_generic": {
    "en": "A music-recognition check found copyrighted music in your video. Allo can't play music its owner hasn't licensed for use here, so the sound was removed.",
    "es": "Una comprobación de reconocimiento musical encontró música con derechos de autor en tu video. Allo no puede reproducir música que su titular no haya licenciado para usarse aquí, así que se quitó el sonido.",
    "fr": "Une vérification de reconnaissance musicale a détecté de la musique protégée dans votre vidéo. Allo ne peut pas diffuser une musique que son ayant droit n'a pas autorisée ici : le son a donc été retiré.",
    "de": "Eine Musikerkennung hat urheberrechtlich geschützte Musik in deinem Video gefunden. Allo darf keine Musik abspielen, die der Rechteinhaber hier nicht lizenziert hat – deshalb wurde der Ton entfernt.",
    "zh": "音乐识别检查在您的视频中发现了受版权保护的音乐。Allo 无法播放版权所有者未授权在此使用的音乐，因此声音已被移除。",
    "ja": "音楽認識チェックにより、動画内に著作権で保護された音楽が見つかりました。Allo では権利者が許諾していない音楽を再生できないため、音声を削除しました。",
    "ru": "Проверка распознавания музыки обнаружила в вашем видео музыку, защищённую авторским правом. Allo не может воспроизводить музыку, которую правообладатель не лицензировал для использования здесь, поэтому звук был удалён.",
    "ar": "وجد فحص التعرف على الموسيقى موسيقى محمية بحقوق النشر في فيديوك. لا يمكن لـ Allo تشغيل موسيقى لم يرخّص مالكها استخدامها هنا، لذا تمت إزالة الصوت.",
    "pt": "Uma verificação de reconhecimento musical encontrou música protegida por direitos autorais no seu vídeo. O Allo não pode reproduzir música que o titular não licenciou para uso aqui, então o som foi removido.",
    "hi": "संगीत पहचान जाँच को आपके वीडियो में कॉपीराइट वाला संगीत मिला। Allo ऐसा संगीत नहीं चला सकता जिसे उसके मालिक ने यहाँ इस्तेमाल के लिए लाइसेंस न दिया हो, इसलिए आवाज़ हटा दी गई।"
  },
  "rights_muted_what_title": {
    "en": "What changed",
    "es": "Qué cambió",
    "fr": "Ce qui a changé",
    "de": "Was sich geändert hat",
    "zh": "发生了什么变化",
    "ja": "何が変わったか",
    "ru": "Что изменилось",
    "ar": "ما الذي تغيّر",
    "pt": "O que mudou",
    "hi": "क्या बदला"
  },
  "rights_muted_what": {
    "en": "Your video is still up and can still be watched — only its sound was removed. Viewers see a \"Sound removed\" notice, and your account is not affected.",
    "es": "Tu video sigue publicado y puede verse; solo se quitó el sonido. Quienes lo ven encuentran un aviso de \"Sonido quitado\", y tu cuenta no se ve afectada.",
    "fr": "Votre vidéo est toujours en ligne et peut être regardée ; seul le son a été retiré. Les spectateurs voient un avis « Son retiré », et votre compte n'est pas concerné.",
    "de": "Dein Video ist weiterhin online und kann angesehen werden – nur der Ton wurde entfernt. Zuschauer sehen den Hinweis „Ton entfernt“, und dein Konto ist nicht betroffen.",
    "zh": "您的视频仍然在线并可观看，只是声音被移除了。观看者会看到\"已移除声音\"的提示，您的账号不受影响。",
    "ja": "動画は引き続き公開され、視聴できます。削除されたのは音声だけです。視聴者には「音声削除済み」の表示が出ます。アカウントへの影響はありません。",
    "ru": "Ваше видео по-прежнему опубликовано и доступно для просмотра — удалён только звук. Зрители видят уведомление «Звук удалён», а ваш аккаунт это не затрагивает.",
    "ar": "ما زال فيديوك منشورًا ويمكن مشاهدته — أُزيل صوته فقط. يرى المشاهدون إشعار \"تمت إزالة الصوت\"، ولا يتأثر حسابك.",
    "pt": "Seu vídeo continua publicado e pode ser assistido — só o som foi removido. Quem assiste vê um aviso de \"Som removido\", e sua conta não é afetada.",
    "hi": "आपका वीडियो अभी भी मौजूद है और देखा जा सकता है — केवल उसकी आवाज़ हटाई गई है। देखने वालों को \"आवाज़ हटाई गई\" सूचना दिखती है, और आपके खाते पर कोई असर नहीं पड़ता।"
  },
  "rights_muted_how_title": {
    "en": "How to fix it",
    "es": "Cómo solucionarlo",
    "fr": "Comment y remédier",
    "de": "So behebst du es",
    "zh": "如何解决",
    "ja": "解決方法",
    "ru": "Как это исправить",
    "ar": "كيف تحل المشكلة",
    "pt": "Como resolver",
    "hi": "इसे कैसे ठीक करें"
  },
  "rights_muted_how": {
    "en": "If you own the music or hold a licence to use it, send a dispute — it opens a conversation with Allo where we'll ask for proof, and a person will review it. Otherwise, post the video again with a song from the Sounds library, which is cleared for use on Allo.",
    "es": "Si la música es tuya o tienes licencia para usarla, envía una disputa: se abrirá una conversación con Allo donde te pediremos pruebas y una persona la revisará. Si no, vuelve a publicar el video con una canción de la biblioteca de Sonidos, autorizada para usarse en Allo.",
    "fr": "Si la musique vous appartient ou si vous détenez une licence, envoyez une contestation : une conversation s'ouvre avec Allo, nous vous demanderons des preuves et une personne l'examinera. Sinon, republiez la vidéo avec un morceau de la bibliothèque Sons, autorisé sur Allo.",
    "de": "Wenn dir die Musik gehört oder du eine Lizenz hast, reiche einen Einspruch ein – er öffnet ein Gespräch mit Allo, in dem wir nach Nachweisen fragen und eine Person es prüft. Andernfalls poste das Video erneut mit einem Song aus der Sounds-Bibliothek, der für Allo freigegeben ist.",
    "zh": "如果这段音乐属于您或您持有使用许可，请提出申诉：这会打开与 Allo 的对话，我们会请您提供证明，并由专人审核。否则，请使用\"声音\"库中的歌曲重新发布视频，这些歌曲已获准在 Allo 上使用。",
    "ja": "その音楽の権利をお持ちか、使用ライセンスをお持ちの場合は、異議を申し立ててください。Allo との会話が開き、証明をお願いしたうえで担当者が確認します。そうでない場合は、Allo で使用が許可されている「サウンド」ライブラリの曲を使って動画を再投稿してください。",
    "ru": "Если музыка принадлежит вам или у вас есть лицензия на её использование, подайте возражение — откроется диалог с Allo, где мы попросим подтверждение, и его рассмотрит человек. Иначе опубликуйте видео снова с треком из библиотеки «Звуки», разрешённой к использованию в Allo.",
    "ar": "إذا كنت تملك الموسيقى أو لديك ترخيص لاستخدامها، أرسل اعتراضًا — سيفتح محادثة مع Allo نطلب فيها إثباتًا، وسيراجعها شخص. وإلا، انشر الفيديو مجددًا مع أغنية من مكتبة الأصوات المصرَّح باستخدامها في Allo.",
    "pt": "Se a música é sua ou você tem licença para usá-la, envie uma contestação — ela abre uma conversa com o Allo, onde pediremos comprovação, e uma pessoa vai analisar. Caso contrário, publique o vídeo de novo com uma música da biblioteca de Sons, liberada para uso no Allo.",
    "hi": "अगर संगीत आपका है या आपके पास इसे इस्तेमाल करने का लाइसेंस है, तो विवाद भेजें — इससे Allo के साथ बातचीत खुलेगी, जहाँ हम प्रमाण माँगेंगे और एक व्यक्ति इसकी समीक्षा करेगा। अन्यथा, Sounds लाइब्रेरी के किसी गीत के साथ वीडियो दोबारा पोस्ट करें, जो Allo पर इस्तेमाल के लिए स्वीकृत है।"
  },
  "rights_dispute_send": {
    "en": "Dispute — I have the rights",
    "es": "Disputar — tengo los derechos",
    "fr": "Contester — j'ai les droits",
    "de": "Einspruch – ich habe die Rechte",
    "zh": "申诉——我拥有版权",
    "ja": "異議申し立て — 権利を持っています",
    "ru": "Возразить — у меня есть права",
    "ar": "اعتراض — أملك الحقوق",
    "pt": "Contestar — tenho os direitos",
    "hi": "विवाद — मेरे पास अधिकार हैं"
  },
  "rights_dispute_chat": {
    "en": "Open the conversation with Allo",
    "es": "Abrir la conversación con Allo",
    "fr": "Ouvrir la conversation avec Allo",
    "de": "Gespräch mit Allo öffnen",
    "zh": "打开与 Allo 的对话",
    "ja": "Allo との会話を開く",
    "ru": "Открыть диалог с Allo",
    "ar": "افتح المحادثة مع Allo",
    "pt": "Abrir a conversa com o Allo",
    "hi": "Allo के साथ बातचीत खोलें"
  },
  "rights_dispute_card": {
    "en": "Copyright dispute",
    "es": "Disputa de derechos de autor",
    "fr": "Contestation de droit d'auteur",
    "de": "Urheberrechts-Einspruch",
    "zh": "版权申诉",
    "ja": "著作権の異議申し立て",
    "ru": "Возражение по авторским правам",
    "ar": "اعتراض على حقوق النشر",
    "pt": "Contestação de direitos autorais",
    "hi": "कॉपीराइट विवाद"
  },
  "rights_dispute_about": {
    "en": "About the sound removed from your video",
    "es": "Sobre el sonido quitado de tu video",
    "fr": "À propos du son retiré de votre vidéo",
    "de": "Zum entfernten Ton deines Videos",
    "zh": "关于您视频中被移除的声音",
    "ja": "動画から削除された音声について",
    "ru": "О звуке, удалённом из вашего видео",
    "ar": "بشأن الصوت الذي أُزيل من فيديوك",
    "pt": "Sobre o som removido do seu vídeo",
    "hi": "आपके वीडियो से हटाई गई आवाज़ के बारे में"
  },
  "rights_req_title": {
    "en": "What we need from you",
    "es": "Qué necesitamos de ti",
    "fr": "Ce dont nous avons besoin",
    "de": "Was wir von dir brauchen",
    "zh": "我们需要您提供",
    "ja": "ご提出いただくもの",
    "ru": "Что нам от вас нужно",
    "ar": "ما نحتاجه منك",
    "pt": "O que precisamos de você",
    "hi": "हमें आपसे क्या चाहिए"
  },
  "rights_status_label": {
    "en": "Status",
    "es": "Estado",
    "fr": "Statut",
    "de": "Status",
    "zh": "状态",
    "ja": "ステータス",
    "ru": "Статус",
    "ar": "الحالة",
    "pt": "Status",
    "hi": "स्थिति"
  },
  "rights_req_proof": {
    "en": "Proof you own the music or hold a licence to use it — a licence, a receipt, or a message from the rights-holder",
    "es": "Prueba de que la música es tuya o de que tienes licencia para usarla: una licencia, un recibo o un mensaje del titular de los derechos",
    "fr": "Une preuve que la musique vous appartient ou que vous détenez une licence : licence, reçu ou message de l'ayant droit",
    "de": "Einen Nachweis, dass dir die Musik gehört oder du eine Lizenz hast – eine Lizenz, eine Quittung oder eine Nachricht des Rechteinhabers",
    "zh": "证明您拥有这段音乐或持有使用许可——许可证、收据或版权所有者的信息",
    "ja": "音楽の権利または使用ライセンスをお持ちであることの証明（ライセンス、領収書、権利者からのメッセージなど）",
    "ru": "Подтверждение, что музыка принадлежит вам или у вас есть лицензия: лицензия, чек или сообщение от правообладателя",
    "ar": "إثبات أنك تملك الموسيقى أو لديك ترخيص لاستخدامها — ترخيص أو إيصال أو رسالة من مالك الحقوق",
    "pt": "Prova de que a música é sua ou de que você tem licença para usá-la — uma licença, um recibo ou uma mensagem do titular dos direitos",
    "hi": "प्रमाण कि संगीत आपका है या आपके पास इसे इस्तेमाल करने का लाइसेंस है — लाइसेंस, रसीद या अधिकार-धारक का संदेश"
  },
  "rights_req_artist": {
    "en": "The artist and the title of the song",
    "es": "El artista y el título de la canción",
    "fr": "L'artiste et le titre du morceau",
    "de": "Künstler und Titel des Songs",
    "zh": "歌曲的演唱者和名称",
    "ja": "曲のアーティスト名とタイトル",
    "ru": "Исполнитель и название песни",
    "ar": "اسم الفنان وعنوان الأغنية",
    "pt": "O artista e o título da música",
    "hi": "गीत का कलाकार और शीर्षक"
  },
  "rights_req_release": {
    "en": "A link to where the music is published or licensed",
    "es": "Un enlace a donde la música está publicada o licenciada",
    "fr": "Un lien vers l'endroit où la musique est publiée ou licenciée",
    "de": "Einen Link dorthin, wo die Musik veröffentlicht oder lizenziert ist",
    "zh": "该音乐发布或授权页面的链接",
    "ja": "その音楽が公開またはライセンスされている場所へのリンク",
    "ru": "Ссылка на место, где музыка опубликована или лицензирована",
    "ar": "رابط إلى المكان الذي نُشرت فيه الموسيقى أو رُخّصت",
    "pt": "Um link para onde a música está publicada ou licenciada",
    "hi": "उस जगह का लिंक जहाँ संगीत प्रकाशित या लाइसेंस किया गया है"
  },
  "rights_req_name": {
    "en": "Your full legal name, so we can record the decision",
    "es": "Tu nombre legal completo, para registrar la decisión",
    "fr": "Votre nom légal complet, pour consigner la décision",
    "de": "Deinen vollständigen Namen, damit wir die Entscheidung festhalten können",
    "zh": "您的法定全名，以便记录处理结果",
    "ja": "決定を記録するための、あなたの正式な氏名",
    "ru": "Ваше полное официальное имя, чтобы зафиксировать решение",
    "ar": "اسمك القانوني الكامل لتسجيل القرار",
    "pt": "Seu nome legal completo, para registrarmos a decisão",
    "hi": "आपका पूरा कानूनी नाम, ताकि हम निर्णय दर्ज कर सकें"
  },
  "rights_status_waiting": {
    "en": "Waiting for review",
    "es": "Pendiente de revisión",
    "fr": "En attente d'examen",
    "de": "Wartet auf Prüfung",
    "zh": "等待审核",
    "ja": "審査待ち",
    "ru": "Ожидает рассмотрения",
    "ar": "بانتظار المراجعة",
    "pt": "Aguardando análise",
    "hi": "समीक्षा की प्रतीक्षा में"
  },
  "rights_status_assigned": {
    "en": "Being reviewed",
    "es": "En revisión",
    "fr": "En cours d'examen",
    "de": "Wird geprüft",
    "zh": "审核中",
    "ja": "審査中",
    "ru": "На рассмотрении",
    "ar": "قيد المراجعة",
    "pt": "Em análise",
    "hi": "समीक्षा जारी है"
  },
  "rights_status_resolved": {
    "en": "Resolved",
    "es": "Resuelta",
    "fr": "Résolue",
    "de": "Erledigt",
    "zh": "已处理",
    "ja": "解決済み",
    "ru": "Решено",
    "ar": "تم الحل",
    "pt": "Resolvida",
    "hi": "हल हो गया"
  },
  "rights_outcome_released": {
    "en": "Sound restored",
    "es": "Sonido restaurado",
    "fr": "Son rétabli",
    "de": "Ton wiederhergestellt",
    "zh": "声音已恢复",
    "ja": "音声を復元しました",
    "ru": "Звук восстановлен",
    "ar": "تمت إعادة الصوت",
    "pt": "Som restaurado",
    "hi": "आवाज़ बहाल की गई"
  },
  "rights_outcome_upheld": {
    "en": "Sound stays removed",
    "es": "El sonido sigue quitado",
    "fr": "Le son reste retiré",
    "de": "Ton bleibt entfernt",
    "zh": "声音仍被移除",
    "ja": "音声は削除のままです",
    "ru": "Звук остаётся удалённым",
    "ar": "يبقى الصوت محذوفًا",
    "pt": "O som continua removido",
    "hi": "आवाज़ हटी रहेगी"
  },
  "vibe_cm_hidden_some": {
    "en": "{{count}} comments hidden because they may be harmful",
    "es": "{{count}} comentarios ocultos porque podrían ser dañinos",
    "fr": "{{count}} commentaires masqués car ils pourraient être blessants",
    "de": "{{count}} Kommentare ausgeblendet, weil sie verletzend sein könnten",
    "zh": "已隐藏 {{count}} 条可能有害的评论",
    "ja": "有害な可能性があるため{{count}}件のコメントを非表示にしました",
    "ru": "{{count}} комментариев скрыто, так как они могут быть оскорбительными",
    "ar": "تم إخفاء {{count}} تعليقات لأنها قد تكون مؤذية",
    "pt": "{{count}} comentários ocultos porque podem ser ofensivos",
    "hi": "{{count}} टिप्पणियाँ छिपाई गईं क्योंकि वे हानिकारक हो सकती हैं"
  },
  "vibe_cm_hidden_one": {
    "en": "1 comment hidden because it may be harmful",
    "es": "1 comentario oculto porque podría ser dañino",
    "fr": "1 commentaire masqué car il pourrait être blessant",
    "de": "1 Kommentar ausgeblendet, weil er verletzend sein könnte",
    "zh": "已隐藏 1 条可能有害的评论",
    "ja": "有害な可能性があるため1件のコメントを非表示にしました",
    "ru": "1 комментарий скрыт, так как он может быть оскорбительным",
    "ar": "تم إخفاء تعليق واحد لأنه قد يكون مؤذيًا",
    "pt": "1 comentário oculto porque pode ser ofensivo",
    "hi": "1 टिप्पणी छिपाई गई क्योंकि वह हानिकारक हो सकती है"
  },
  "vibe_cm_hidden_show": {
    "en": "Show",
    "es": "Mostrar",
    "fr": "Afficher",
    "de": "Anzeigen",
    "zh": "显示",
    "ja": "表示",
    "ru": "Показать",
    "ar": "إظهار",
    "pt": "Mostrar",
    "hi": "दिखाएँ"
  },
  "vibe_cm_hidden_hide": {
    "en": "Hide again",
    "es": "Ocultar de nuevo",
    "fr": "Masquer à nouveau",
    "de": "Wieder ausblenden",
    "zh": "重新隐藏",
    "ja": "再び非表示",
    "ru": "Скрыть снова",
    "ar": "إخفاء مجددًا",
    "pt": "Ocultar de novo",
    "hi": "फिर से छिपाएँ"
  },
  "vibe_cm_yours_hidden": {
    "en": "Only you can see this — it may be hurtful to others",
    "es": "Solo tú puedes ver esto: podría herir a otras personas",
    "fr": "Vous seul pouvez voir ceci — cela pourrait blesser d’autres personnes",
    "de": "Nur du siehst das – es könnte andere verletzen",
    "zh": "只有你能看到这条评论——它可能会伤害他人",
    "ja": "これはあなたにだけ表示されています — 他の人を傷つける可能性があります",
    "ru": "Это видите только вы — комментарий может задеть других",
    "ar": "أنت وحدك من يرى هذا — قد يؤذي الآخرين",
    "pt": "Só você pode ver isto — pode magoar outras pessoas",
    "hi": "इसे केवल आप देख सकते हैं — यह दूसरों को आहत कर सकती है"
  },
  "vibe_cm_hidden_tag": {
    "en": "Hidden",
    "es": "Oculto",
    "fr": "Masqué",
    "de": "Ausgeblendet",
    "zh": "已隐藏",
    "ja": "非表示",
    "ru": "Скрыто",
    "ar": "مخفي",
    "pt": "Oculto",
    "hi": "छिपी हुई"
  },
  "vibe_cm_reply_gone": {
    "en": "The comment this answered was removed",
    "es": "El comentario al que respondía fue eliminado",
    "fr": "Le commentaire auquel ceci répondait a été supprimé",
    "de": "Der Kommentar, auf den das antwortete, wurde entfernt",
    "zh": "所回复的评论已被删除",
    "ja": "返信先のコメントは削除されました",
    "ru": "Комментарий, на который это был ответ, удалён",
    "ar": "تم حذف التعليق الذي كان هذا ردًا عليه",
    "pt": "O comentário que isto respondia foi removido",
    "hi": "जिस टिप्पणी का यह जवाब था, उसे हटा दिया गया"
  },
  "vibe_cm_too_long": {
    "en": "Comments can be up to 1,000 characters",
    "es": "Los comentarios pueden tener hasta 1.000 caracteres",
    "fr": "Les commentaires peuvent contenir jusqu’à 1 000 caractères",
    "de": "Kommentare dürfen bis zu 1.000 Zeichen lang sein",
    "zh": "评论最多可输入 1,000 个字符",
    "ja": "コメントは1,000文字までです",
    "ru": "Комментарий может содержать до 1 000 символов",
    "ar": "يمكن أن يصل التعليق إلى 1000 حرف",
    "pt": "Os comentários podem ter até 1.000 caracteres",
    "hi": "टिप्पणी अधिकतम 1,000 वर्णों की हो सकती है"
  },
  "vibe_cm_posting": {
    "en": "Posting…",
    "es": "Publicando…",
    "fr": "Publication…",
    "de": "Wird gesendet…",
    "zh": "发布中…",
    "ja": "投稿中…",
    "ru": "Отправка…",
    "ar": "جارٍ النشر…",
    "pt": "Publicando…",
    "hi": "पोस्ट हो रहा है…"
  },
  "vibe_cm_failed": {
    "en": "Failed to post",
    "es": "No se pudo publicar",
    "fr": "Échec de la publication",
    "de": "Senden fehlgeschlagen",
    "zh": "发布失败",
    "ja": "投稿できませんでした",
    "ru": "Не удалось отправить",
    "ar": "تعذّر النشر",
    "pt": "Falha ao publicar",
    "hi": "पोस्ट नहीं हो सका"
  },
  "vibe_cm_pin": {
    "en": "Pin",
    "es": "Fijar",
    "fr": "Épingler",
    "de": "Anheften",
    "zh": "置顶",
    "ja": "固定",
    "ru": "Закрепить",
    "ar": "تثبيت",
    "pt": "Fixar",
    "hi": "पिन करें"
  },
  "vibe_cm_unpin": {
    "en": "Unpin",
    "es": "Desfijar",
    "fr": "Désépingler",
    "de": "Lösen",
    "zh": "取消置顶",
    "ja": "固定を解除",
    "ru": "Открепить",
    "ar": "إلغاء التثبيت",
    "pt": "Desafixar",
    "hi": "अनपिन करें"
  },
  "vibe_cm_pinned": {
    "en": "Pinned",
    "es": "Fijado",
    "fr": "Épinglé",
    "de": "Angeheftet",
    "zh": "已置顶",
    "ja": "固定済み",
    "ru": "Закреплено",
    "ar": "مثبّت",
    "pt": "Fixado",
    "hi": "पिन किया गया"
  },
  "vibe_cm_pin_failed": {
    "en": "That comment could not be pinned.",
    "es": "No se pudo fijar ese comentario.",
    "fr": "Impossible d’épingler ce commentaire.",
    "de": "Der Kommentar konnte nicht angeheftet werden.",
    "zh": "无法置顶该评论。",
    "ja": "このコメントを固定できませんでした。",
    "ru": "Не удалось закрепить комментарий.",
    "ar": "تعذّر تثبيت هذا التعليق.",
    "pt": "Não foi possível fixar esse comentário.",
    "hi": "यह टिप्पणी पिन नहीं हो सकी।"
  },
  "vibe_cm_owner_only": {
    "en": "Comments are off — only you can comment",
    "es": "Comentarios desactivados: solo tú puedes comentar",
    "fr": "Commentaires désactivés — vous seul pouvez commenter",
    "de": "Kommentare sind aus – nur du kannst kommentieren",
    "zh": "评论已关闭 — 只有你可以评论",
    "ja": "コメントはオフです — あなただけがコメントできます",
    "ru": "Комментарии отключены — комментировать можете только вы",
    "ar": "التعليقات متوقفة — يمكنك أنت فقط التعليق",
    "pt": "Comentários desativados — só você pode comentar",
    "hi": "टिप्पणियाँ बंद हैं — केवल आप टिप्पणी कर सकते हैं"
  },
  "vibe_cm_retry": {
    "en": "Retry",
    "es": "Reintentar",
    "fr": "Réessayer",
    "de": "Erneut versuchen",
    "zh": "重试",
    "ja": "再試行",
    "ru": "Повторить",
    "ar": "إعادة المحاولة",
    "pt": "Tentar de novo",
    "hi": "फिर कोशिश करें"
  },
  "vibe_cm_just_now": {
    "en": "just now",
    "es": "justo ahora",
    "fr": "à l’instant",
    "de": "gerade eben",
    "zh": "刚刚",
    "ja": "たった今",
    "ru": "только что",
    "ar": "الآن",
    "pt": "agora mesmo",
    "hi": "अभी-अभी"
  },
  "vibe_cm_view_replies": {
    "en": "View {{count}} replies",
    "es": "Ver {{count}} respuestas",
    "fr": "Voir {{count}} réponses",
    "de": "{{count}} Antworten anzeigen",
    "zh": "查看 {{count}} 条回复",
    "ja": "返信{{count}}件を表示",
    "ru": "Показать ответы ({{count}})",
    "ar": "عرض {{count}} ردود",
    "pt": "Ver {{count}} respostas",
    "hi": "{{count}} जवाब देखें"
  },
  "vibe_ui_comments_count": {
    "en": "{{count}} comments",
    "es": "{{count}} comentarios",
    "fr": "{{count}} commentaires",
    "de": "{{count}} Kommentare",
    "zh": "{{count}} 条评论",
    "ja": "コメント {{count}} 件",
    "ru": "{{count}} комментариев",
    "ar": "{{count}} تعليق",
    "pt": "{{count}} comentários",
    "hi": "{{count}} टिप्पणियाँ"
  },
  "vibe_cm_sort": {
    "en": "Sort comments",
    "es": "Ordenar comentarios",
    "fr": "Trier les commentaires",
    "de": "Kommentare sortieren",
    "zh": "评论排序",
    "ja": "コメントの並び替え",
    "ru": "Сортировка комментариев",
    "ar": "ترتيب التعليقات",
    "pt": "Ordenar comentários",
    "hi": "टिप्पणियाँ क्रमबद्ध करें"
  },
  "vibe_cm_sort_top": {
    "en": "Top",
    "es": "Destacados",
    "fr": "Populaires",
    "de": "Top",
    "zh": "热门",
    "ja": "人気順",
    "ru": "Популярные",
    "ar": "الأفضل",
    "pt": "Principais",
    "hi": "शीर्ष"
  },
  "vibe_cm_sort_newest": {
    "en": "Newest",
    "es": "Más recientes",
    "fr": "Plus récents",
    "de": "Neueste",
    "zh": "最新",
    "ja": "新着順",
    "ru": "Новые",
    "ar": "الأحدث",
    "pt": "Mais recentes",
    "hi": "नवीनतम"
  },
  "vibe_cm_view_reply": {
    "en": "View 1 reply",
    "es": "Ver 1 respuesta",
    "fr": "Voir 1 réponse",
    "de": "1 Antwort anzeigen",
    "zh": "查看 1 条回复",
    "ja": "返信1件を表示",
    "ru": "Показать 1 ответ",
    "ar": "عرض رد واحد",
    "pt": "Ver 1 resposta",
    "hi": "1 जवाब देखें"
  },
  "vibe_cm_view_more": {
    "en": "View {{count}} more replies",
    "es": "Ver {{count}} respuestas más",
    "fr": "Voir {{count}} réponses de plus",
    "de": "{{count}} weitere Antworten anzeigen",
    "zh": "再查看 {{count}} 条回复",
    "ja": "さらに返信{{count}}件を表示",
    "ru": "Показать ещё {{count}} ответов",
    "ar": "عرض {{count}} ردود أخرى",
    "pt": "Ver mais {{count}} respostas",
    "hi": "{{count}} और जवाब देखें"
  },
  "vibe_cm_hide_replies": {
    "en": "Hide replies",
    "es": "Ocultar respuestas",
    "fr": "Masquer les réponses",
    "de": "Antworten ausblenden",
    "zh": "收起回复",
    "ja": "返信を非表示",
    "ru": "Скрыть ответы",
    "ar": "إخفاء الردود",
    "pt": "Ocultar respostas",
    "hi": "जवाब छिपाएँ"
  },
  "vibe_cm_like": {
    "en": "Like",
    "es": "Me gusta",
    "fr": "J’aime",
    "de": "Gefällt mir",
    "zh": "赞",
    "ja": "いいね",
    "ru": "Нравится",
    "ar": "إعجاب",
    "pt": "Curtir",
    "hi": "पसंद"
  },
  "vibe_cm_dislike": {
    "en": "Dislike",
    "es": "No me gusta",
    "fr": "Je n’aime pas",
    "de": "Gefällt mir nicht",
    "zh": "不赞",
    "ja": "よくないね",
    "ru": "Не нравится",
    "ar": "عدم إعجاب",
    "pt": "Não curtir",
    "hi": "नापसंद"
  },
  "vibe_cm_copy_link": {
    "en": "Copy link",
    "es": "Copiar enlace",
    "fr": "Copier le lien",
    "de": "Link kopieren",
    "zh": "复制链接",
    "ja": "リンクをコピー",
    "ru": "Скопировать ссылку",
    "ar": "نسخ الرابط",
    "pt": "Copiar link",
    "hi": "लिंक कॉपी करें"
  },
  "vibe_cm_link_copied": {
    "en": "Link copied",
    "es": "Enlace copiado",
    "fr": "Lien copié",
    "de": "Link kopiert",
    "zh": "链接已复制",
    "ja": "リンクをコピーしました",
    "ru": "Ссылка скопирована",
    "ar": "تم نسخ الرابط",
    "pt": "Link copiado",
    "hi": "लिंक कॉपी हो गया"
  },
  "vibe_cm_copy_text": {
    "en": "Copy text",
    "es": "Copiar texto",
    "fr": "Copier le texte",
    "de": "Text kopieren",
    "zh": "复制文字",
    "ja": "テキストをコピー",
    "ru": "Копировать текст",
    "ar": "نسخ النص",
    "pt": "Copiar texto",
    "hi": "टेक्स्ट कॉपी करें"
  },
  "vibe_cm_text_copied": {
    "en": "Copied",
    "es": "Copiado",
    "fr": "Copié",
    "de": "Kopiert",
    "zh": "已复制",
    "ja": "コピーしました",
    "ru": "Скопировано",
    "ar": "تم النسخ",
    "pt": "Copiado",
    "hi": "कॉपी किया गया"
  }
};

const LANGS = ["en","es","fr","de","zh","ja","ru","ar","pt","hi"];

/** The browser's language, narrowed to one we actually have. Defaults to English. */
export function pickLang(nav) {
  const raw = String((nav ?? (typeof navigator !== 'undefined' ? navigator.language : '')) || '').toLowerCase();
  const base = raw.split(/[-_]/)[0];
  return LANGS.includes(base) ? base : 'en';
}

/**
 * One moderation string, in `lang` (default: the browser's).
 * Falls back to English, then to '' — never to the key name.
 *
 * 🟥 `params` SUBSTITUTES {{name}} PLACEHOLDERS, and it was missing. The reason
 *    line reads "Reason: {{reason}}" in every language — without substitution
 *    the browser rendered those eight characters literally, next to a perfectly
 *    good translation, which looks like the page is broken rather than like a
 *    string is unfilled. Same {{…}} convention as the app's i18n and the
 *    server's L(), so one wording works in all three.
 */
export function mt(key, lang, params) {
  const row = T[key];
  if (!row) return '';
  const l = lang || pickLang();
  let s = row[l] || row.en || '';
  if (params) {
    for (const k of Object.keys(params)) s = s.split(`{{${k}}}`).join(String(params[k]));
  }
  return s;
}

/* 🟥 THE ID → WORDING MAP. The notification stores a neutral `reasonId`; each
   client owns its own strings. A moderator's CUSTOM reason has no id and never
   comes through here — it is shown verbatim. */
export const MOD_REASON_KEY = {
  nudity: 'vibe_report_nudes',
  violence: 'vibe_report_violence',
  hate: 'vibe_report_hatred',
  spam: 'vibe_report_spam',
  other: 'vibe_report_other',
};

/** The label for a stored reason — verbatim custom text, or a translated id. */
export function reasonLabel(n, lang) {
  if (!n) return '';
  if (n.reasonText) return String(n.reasonText);
  const key = MOD_REASON_KEY[n.reasonId];
  return key ? mt(key, lang) : '';
}

export const MOD_LANGS = LANGS;
